import { Vec3 } from '@/core/math/Vec3';
import { clamp, clamp01, moveTowards } from '@/core/math/scalar';
import { g0 } from '@/core/units';
import { EngineDef } from './ShipDef';

const STANDARD_PRESSURE = 101325;

export interface EngineEnvironment {
  /** Ambient pressure, Pa. */
  pressure: number;
  /** Ambient density, kg/m^3. */
  density: number;
}

/**
 * Runtime state for a single engine.
 *
 * Engines are never "on" instantly. `commandedThrottle` is what the pilot or
 * flight computer asks for; `throttle` slews toward it at the engine's rate.
 * That lag is why a nuclear thermal hauler cannot be flown like a fighter, and
 * why the landing assist has to lead its corrections.
 */
export class Engine {
  readonly def: EngineDef;
  /** Current actual throttle, 0..1. */
  throttle = 0;
  /** Requested throttle, 0..1. */
  commandedThrottle = 0;
  /** Whether the engine is available at all. */
  enabled = true;
  /** 0 = pristine, 1 = destroyed. Reduces thrust and biases it off-axis. */
  damage = 0;
  /** Gimbal deflection in body radians, x = pitch, y = yaw. */
  readonly gimbal = new Vec3();
  /** Fraction of demanded power actually delivered last step, 0..1. */
  powerSatisfaction = 1;

  /** Thrust produced last step, newtons. */
  lastThrust = 0;
  /** Propellant consumed last step, kg/s. */
  lastMassFlow = 0;

  readonly mountPosition: Vec3;
  private readonly baseDirection: Vec3;

  constructor(def: EngineDef) {
    this.def = def;
    this.mountPosition = new Vec3(def.position[0], def.position[1], def.position[2]);
    this.baseDirection = new Vec3(def.direction[0], def.direction[1], def.direction[2]).normalize();
  }

  get id(): string {
    return this.def.id;
  }

  get isAirBreathing(): boolean {
    return this.def.airBreathingReferenceDensity !== undefined;
  }

  get isRcs(): boolean {
    return this.def.type === 'rcs';
  }

  /** Specific impulse at the given ambient pressure, seconds. */
  ispAt(env: EngineEnvironment): number {
    const d = this.def;
    if (this.isAirBreathing) return d.ispSeaLevel;
    if (d.ispVacuum === d.ispSeaLevel) return d.ispVacuum;
    const ratio = clamp01(env.pressure / STANDARD_PRESSURE);
    return d.ispVacuum + (d.ispSeaLevel - d.ispVacuum) * ratio;
  }

  /**
   * Maximum thrust available in the current environment, newtons.
   *
   * Pressure-fed rockets lose thrust as ambient pressure rises (back-pressure
   * on the nozzle). Air-breathing turbines do the opposite: they need air, so
   * their thrust falls to zero in vacuum.
   */
  maxThrustAt(env: EngineEnvironment): number {
    const d = this.def;
    if (!this.enabled || this.damage >= 1) return 0;
    const health = 1 - this.damage;

    if (this.isAirBreathing) {
      const ref = d.airBreathingReferenceDensity ?? 1.2;
      const airFactor = clamp(env.density / ref, 0, 1.15);
      return d.maxThrust * airFactor * health;
    }
    const pressureFactor = d.ispVacuum > 0 ? this.ispAt(env) / d.ispVacuum : 1;
    return d.maxThrust * pressureFactor * health;
  }

  /** Slews the throttle toward the command and clamps it to the valid band. */
  update(dt: number): void {
    const d = this.def;
    let target = clamp01(this.commandedThrottle);
    if (target > 0 && target < d.minThrottle) target = d.minThrottle;
    if (!this.enabled || this.damage >= 1) target = 0;
    this.throttle = moveTowards(this.throttle, target, d.throttleRate * dt);
    if (this.throttle < 1e-4) this.throttle = 0;
  }

  /** Thrust direction in body coordinates, including gimbal and damage bias. */
  thrustDirection(out = new Vec3()): Vec3 {
    out.copy(this.baseDirection);
    const range = this.def.gimbalRange;
    if (range > 0 && (this.gimbal.x !== 0 || this.gimbal.y !== 0)) {
      const gx = clamp(this.gimbal.x, -range, range);
      const gy = clamp(this.gimbal.y, -range, range);
      // Small-angle deflection about the two axes perpendicular to the nozzle.
      const perpA = Vec3.anyPerpendicular(this.baseDirection);
      const perpB = Vec3.crossVectors(this.baseDirection, perpA).normalize();
      out.addScaled(perpA, Math.tan(gx)).addScaled(perpB, Math.tan(gy)).normalize();
    }
    if (this.damage > 0.05) {
      // A damaged nozzle no longer thrusts cleanly along its axis, which is what
      // makes asymmetric-thrust failures interesting to fly.
      const skew = this.damage * 0.18;
      const perp = Vec3.anyPerpendicular(this.baseDirection);
      out.addScaled(perp, skew).normalize();
    }
    return out;
  }

  /** Current thrust magnitude, newtons. */
  thrustMagnitude(env: EngineEnvironment): number {
    return this.maxThrustAt(env) * this.throttle * clamp01(this.powerSatisfaction);
  }

  /** Propellant mass flow at the current throttle, kg/s. */
  massFlow(env: EngineEnvironment): number {
    const isp = this.ispAt(env);
    if (isp <= 0) return 0;
    return this.thrustMagnitude(env) / (isp * g0);
  }

  /** Electrical power demand at the current throttle, watts. */
  powerDemand(): number {
    return this.def.powerDraw * this.throttle;
  }

  /** Waste heat at the current throttle, watts. */
  heatOutput(): number {
    return this.def.heatOutput * this.throttle;
  }

  /**
   * Records the thrust and flow actually produced this step so the HUD and the
   * delta-v readout do not have to recompute them.
   */
  record(thrust: number, flow: number): void {
    this.lastThrust = thrust;
    this.lastMassFlow = flow;
  }
}

/** Total thrust an engine set can produce along a body direction, newtons. */
export function availableThrustAlong(engines: Engine[], bodyDirection: Vec3, env: EngineEnvironment): number {
  const dir = bodyDirection.normalized();
  let total = 0;
  const scratch = new Vec3();
  for (const e of engines) {
    const d = e.thrustDirection(scratch);
    const alignment = d.dot(dir);
    if (alignment > 0) total += e.maxThrustAt(env) * alignment;
  }
  return total;
}
